// Duplexer-vs-SSE reconciliation — pure helpers shared by the chat state
// (use-chat). Every SCHEDULER assistant row is delivered twice with the SAME
// persisted id: once live over SSE (`messageEnd` stamps it as `finalId`) and
// again via the duplexer republish (`entity.id`). The client keeps ONE bubble
// by matching those ids; these helpers decide whether an incoming duplexer
// entity is already on screen so it is never painted a second time.

import { stripLeakedFraming } from './leaked-framing';

type DedupMessage = {
  id?: string;
  finalId?: string;
  role?: string;
  content?: string;
  animate?: boolean;
};

type DedupEntity = { id?: string; role?: string; content?: string };

/**
 * A streamed block whose `messageEnd` never arrived — the stream was cut mid-turn (an upstream
 * timeout, a proxy drop, an abort). It is recognisable by what it LACKS: born from `messageStart`
 * this session (`animate`), but never stamped with the persisted id (`finalId`) that only the End
 * event carries. So the duplexer's id-keyed dedupe cannot collapse its twin, and its content is a
 * prefix of the server's.
 *
 * `animate` is deliberately NOT the test on its own: it is the typewriter-enable flag and stays set
 * for the row's whole life, so every settled streamed bubble on screen has it. `finalId` is what
 * separates "still open" from "closed" — see `MessageRow`'s `useTypewriter(content, !animate)`, and
 * the note in orion-web/CLAUDE.md about why clearing `animate` at `messageEnd` truncates the text.
 */
export function isOrphanedStreamBlock(m: DedupMessage): boolean {
  return !!m.animate && !m.finalId && (m.role === 'ASSISTANT' || m.role === 'ASSISTANT_STEP');
}

/** True when an entity's persisted id is already a row's id or its streamed `finalId`. */
export function isEntityShownById(messages: DedupMessage[], entityId: string): boolean {
  return messages.some((m) => m.id === entityId || m.finalId === entityId);
}

/**
 * True when a live-streamed bubble already shows this assistant text, so the
 * duplexer republish of the same row must not append a second identical bubble.
 *
 * The id match ({@link isEntityShownById}) is the happy path: `messageEnd`
 * stamps `finalId` and the twin dedupes cleanly. This content guard is the
 * safety net for when that link is lost — a truncated / raced SSE where
 * `messageEnd` never landed, so the streamed bubble has NO `finalId` and the id
 * check misses. Without it the duplexer twin appends as a byte-identical
 * duplicate the post-turn refetch can no longer collapse (our list is now
 * longer than the server's, so it declines to replace).
 *
 * Scoped to streamed bubbles — those carrying a persisted `finalId` OR still
 * flagged `animate` (born from `messageStart` this session, i.e. the
 * finalId-lost case). History-loaded rows carry neither, so a genuinely
 * repeated line from an earlier turn is never dropped.
 *
 * The comparison is on the SANITIZED body, not the raw bytes, because the two
 * sides of this match are not stored the same way: the streamed bubble holds
 * the model's raw deltas, while the harness persists
 * `stripLeakedFraming(...)` of that same text before the row is saved
 * (`streaming-agentic-turn.ts`). So exactly when the model leaked a
 * `<system>`/`<metadata>` block — the case the strip exists for — the raw
 * bytes differ, the guard missed, and the duplexer twin of a row the reader is
 * already looking at appended as a second bubble. `stripLeakedFraming` is
 * idempotent and 1:1 with the server's, so stripping both sides is safe; the
 * raw equality is kept as the fast path. (`repairComposioConnectLinks`, the
 * harness's other persist-side repair, has no web mirror — a connect link
 * rewritten on persist can still slip past this.)
 */
export function isStreamedDuplicate(messages: DedupMessage[], entity: DedupEntity): boolean {
  if (!entity.content) return false;
  const entityBody = stripLeakedFraming(entity.content);
  return messages.some(
    (m) =>
      (!!m.finalId || m.animate === true) &&
      m.role === entity.role &&
      (m.content === entity.content || (!!m.content && stripLeakedFraming(m.content) === entityBody)),
  );
}
